/**
 * Shared avvikelser (portfolio flag) definitions.
 *
 * A single source of truth for the per-site flags surfaced across the dashboard,
 * the BRF list, and the site detail banner. The chip look (SV label, severity
 * ink, and 12%-alpha background) lived inline in the dashboard PortfolioTable;
 * it is centralised here so every surface stays visually identical.
 */

import type { PortfolioFlag } from '@/features/dashboard/api';

export type FlagSeverity = 'crit' | 'warn' | 'admin';

export interface FlagMeta {
  /** Short SV chip label, e.g. OBEMANNAT. */
  label: string;
  /** One-line SV description used in the overflow popover. */
  description: string;
  severity: FlagSeverity;
  /** Severity ink for text/border. */
  color: string;
  /** Same ink at 0.12 alpha for the chip background. */
  bg: string;
}

// crit → #C24138 · warn → #B97E1F · admin/muted → #607C6E
export const FLAG_META: Record<PortfolioFlag, FlagMeta> = {
  unassigned_7d: {
    label: 'OBEMANNAT',
    description: 'Otilldelade jobb 7d',
    severity: 'crit',
    color: '#C24138',
    bg: 'rgba(194,65,56,0.12)',
  },
  qc_failed_30d: {
    label: 'UK',
    description: 'Underkänd kvalitetskontroll',
    severity: 'crit',
    color: '#C24138',
    bg: 'rgba(194,65,56,0.12)',
  },
  missing_price: {
    label: 'PRIS',
    description: 'Prissättning saknas',
    severity: 'warn',
    color: '#B97E1F',
    bg: 'rgba(185,126,31,0.12)',
  },
  missing_points: {
    label: 'POÄNG',
    description: 'Poäng saknas på block',
    severity: 'warn',
    color: '#B97E1F',
    bg: 'rgba(185,126,31,0.12)',
  },
  no_jobs_30d: {
    label: 'INGA JOBB',
    description: 'Inga jobb 30d',
    severity: 'warn',
    color: '#B97E1F',
    bg: 'rgba(185,126,31,0.12)',
  },
  missing_fortnox: {
    label: 'FORTNOX',
    description: 'Fortnox kundnr saknas',
    severity: 'admin',
    color: '#607C6E',
    bg: 'rgba(96,124,110,0.12)',
  },
};

/** Severity-ordered: crit → warn → admin. A site can carry all six. */
export const FLAG_ORDER: PortfolioFlag[] = [
  'unassigned_7d',
  'qc_failed_30d',
  'missing_price',
  'missing_points',
  'no_jobs_30d',
  'missing_fortnox',
];

/** Flags re-sorted into severity order, ignoring anything unrecognised. */
export function orderFlags(flags: PortfolioFlag[]): PortfolioFlag[] {
  return FLAG_ORDER.filter((f) => flags.includes(f));
}

/**
 * Deep link to WHERE the flag is fixed. Tab keys match PlanningWorkspace
 * detailTabs (general | blocks | extras | calendar | jobs | pricing | print);
 * qc_failed_30d routes to the QC view instead of the site detail.
 */
export function flagFixRoute(siteId: string, flag: PortfolioFlag): string {
  switch (flag) {
    case 'missing_price':
      return `#/brfs/${siteId}?tab=pricing`;
    case 'missing_fortnox':
      return `#/brfs/${siteId}?tab=general`;
    case 'missing_points':
      return `#/brfs/${siteId}?tab=blocks`;
    case 'no_jobs_30d':
      return `#/brfs/${siteId}?tab=blocks`;
    case 'unassigned_7d':
      return `#/brfs/${siteId}?tab=jobs`;
    case 'qc_failed_30d':
      return `#/qc/${siteId}`;
  }
}
